import { useEffect, useRef } from 'react';
import { devLog } from '@utils/devLog';
import { apiPost } from './api';

type TrackPayload = {
	event_name: string;
	properties?: Record<string, unknown>;
	duration_seconds?: number;
	page_path?: string;
};

const SESSION_KEY = 'gardenhub:activity:session_id';
const ANON_KEY = 'gardenhub:activity:anonymous_id';
/** Visible-tab heartbeat interval (keeps row volume ~4× lower than 15s). */
const HEARTBEAT_INTERVAL_MS = 60_000;
const HEARTBEAT_DURATION_SECONDS = HEARTBEAT_INTERVAL_MS / 1000;

function randomId(prefix: string): string {
	return `${prefix}-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

function getStorageId(key: string, prefix: string, fallbackStore: Storage): string {
	const existing = fallbackStore.getItem(key);
	if (existing) return existing;
	const next = randomId(prefix);
	fallbackStore.setItem(key, next);
	return next;
}

function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
	const ua = navigator.userAgent.toLowerCase();
	if (/tablet|ipad/.test(ua)) return 'tablet';
	if (/mobi|android|iphone/.test(ua)) return 'mobile';
	return 'desktop';
}

function detectBrowser(): string {
	const ua = navigator.userAgent;
	if (ua.includes('Edg/')) return 'edge';
	if (ua.includes('Chrome/')) return 'chrome';
	if (ua.includes('Firefox/')) return 'firefox';
	if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'safari';
	return 'other';
}

function getCurrentUserId(): number | null {
	try {
		const raw = sessionStorage.getItem('gardenhub:auth');
		if (!raw) return null;
		const parsed = JSON.parse(raw) as { id?: string | number } | null;
		if (!parsed?.id) return null;
		const n = Number(parsed.id);
		return Number.isFinite(n) ? n : null;
	} catch {
		return null;
	}
}

export async function trackActivityEvent(payload: TrackPayload): Promise<void> {
	if (typeof window === 'undefined') return;
	try {
		const sessionId = getStorageId(SESSION_KEY, 'sess', sessionStorage);
		const anonymousId = getStorageId(ANON_KEY, 'anon', localStorage);
		const userId = getCurrentUserId();
		const params = new URLSearchParams(window.location.search);

		await apiPost('/activity-events', {
			event_name: payload.event_name,
			page_path: payload.page_path ?? window.location.pathname,
			session_id: sessionId,
			anonymous_id: anonymousId,
			user_id: userId ?? undefined,
			is_logged_in: !!userId,
			duration_seconds: payload.duration_seconds ?? undefined,
			referrer: document.referrer || undefined,
			utm_source: params.get('utm_source') || undefined,
			utm_medium: params.get('utm_medium') || undefined,
			utm_campaign: params.get('utm_campaign') || undefined,
			device_type: detectDeviceType(),
			browser: detectBrowser(),
			locale: navigator.language || undefined,
			properties: payload.properties ?? undefined
		});
	} catch (error) {
		// Never block UX for analytics failures.
		devLog('activity tracker skipped', error);
	}
}

export function usePageActivityTracker(pageName: string): void {
	const activeStartedAtRef = useRef<number | null>(null);
	const activeDurationMsRef = useRef(0);

	useEffect(() => {
		void trackActivityEvent({
			event_name: `${pageName}_view`
		});
		activeStartedAtRef.current = Date.now();

		const heartbeat = window.setInterval(() => {
			if (document.visibilityState !== 'visible') return;
			void trackActivityEvent({
				event_name: 'session_heartbeat',
				duration_seconds: HEARTBEAT_DURATION_SECONDS,
				properties: { page_name: pageName }
			});
		}, HEARTBEAT_INTERVAL_MS);

		const onVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				if (activeStartedAtRef.current) {
					activeDurationMsRef.current += Date.now() - activeStartedAtRef.current;
					activeStartedAtRef.current = null;
				}
				return;
			}
			activeStartedAtRef.current = Date.now();
		};
		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			window.clearInterval(heartbeat);
			document.removeEventListener('visibilitychange', onVisibilityChange);
			if (activeStartedAtRef.current) {
				activeDurationMsRef.current += Date.now() - activeStartedAtRef.current;
			}
			const totalSeconds = Math.round(activeDurationMsRef.current / 1000);
			void trackActivityEvent({
				event_name: `${pageName}_exit`,
				duration_seconds: totalSeconds,
				properties: { page_name: pageName }
			});
		};
	}, [pageName]);
}
